import type { FormElement, SubmissionResponse } from '../types/formSubmission';
import { handleRecaptchaVerification } from '../utils/reCAPTCHA';

/**
 * Submits the form data to the server.
 *
 * @param {FormData} formData - The form data to submit
 * @param {string} endpoint - The specific endpoint for the form service (e.g., "Pointa")
 * @returns {Promise<SubmissionResponse>} The submission result and any error
 *
 * @example
 * ```typescript
 * const formData = {
 *   first_name: "John",
 *   last_name: "Doe",
 *   email: "john@example.com",
 *   message: "Hello"
 * };
 *
 * const { data, error } = await submitContactFormData(formData, "Pointa");
 * if (error) {
 *   console.error('Submission failed:', error);
 *   return;
 * }
 * // Handle successful submission
 * ```
 */
export async function submitForm(formData: FormElement[], endpoint: string): Promise<SubmissionResponse> {
  // Consider moving this to an environment config
  const BASE_URL =
    'https://consumers-gateway-4cygfe52ia-ew.a.run.app:443/pointa.pa.services.public.v1.FormsService';
  const API_URL = `${BASE_URL}/${endpoint}`;

  // Convert to reduce for better readability
  const payload = formData.reduce((acc, { formProperty, formValue }) => ({
    ...acc,
    [formProperty]: formValue
  }), {} as Record<string, string | number | boolean>);

  // Use async/await instead of Promise chains
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Request failed with status: ${response.status}`);
    }

    const responseData = await response.json();
    return { data: responseData, error: null };

  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Initializes and handles the form submission process.
 * This function sets up event listeners, manages form visibility,
 * performs reCAPTCHA verification, and submits form data to the server.
 *
 * @param {string} formTargetId - The ID of the form element to initialize
 * @param {string} endpoint - The API endpoint to submit the form data to
 * @param {string} siteKey - The reCAPTCHA site key for verification
 * @throws {Error} Throws an error if any step in the form submission process fails
 */
export function initializeForm(formTargetId: string, endpoint: string, siteKey: string) {
  console.log('Initializing form', formTargetId, endpoint, siteKey);
  // Define state variables at the top
  let errorDiv: HTMLElement | null;
  let successDiv: HTMLElement | null;
  let submitButton: HTMLButtonElement | null;
  let contactRequestForm: HTMLFormElement | null;

  const showError = () => {
    if (errorDiv) errorDiv.style.display = 'block';
    if (submitButton) {
      submitButton.value = 'Submit';
      submitButton.disabled = false;
    }
  };

  const showSuccess = () => {
    if (contactRequestForm) contactRequestForm.style.display = 'none';
    if (successDiv) successDiv.style.display = 'block';
  };


  const handleFormSubmission = async (event: SubmitEvent) => {
    event.preventDefault();

    console.log("event", event)

    if (!contactRequestForm || !submitButton) return;

    submitButton.value = 'Sending...';
    submitButton.disabled = true;

    try {
      await handleRecaptchaVerification(siteKey);

      const formData = new FormData(contactRequestForm);
      const formElements: FormElement[] = Array.from(formData.entries()).map(([key, value]) => ({
        formProperty: key,
        formValue: value as string
      }));

      const { error: submitError } = await submitForm(formElements, endpoint);

      if (submitError) {
        console.error('Submit error:', submitError);
        throw new Error('Form submission failed');
      }

      showSuccess();
    } catch (e) {
      console.error('Form submission error:', e);
      showError();
    }
  };

  const setup = () => {
    contactRequestForm = document.getElementById(formTargetId) as HTMLFormElement;
    
    if (!contactRequestForm?.parentElement) return;

    // Remove Webflow form class
    contactRequestForm.parentElement.classList.remove('w-form');

    // Initialize form elements
    errorDiv = contactRequestForm.parentElement.querySelector('[data-name-form="error"]');
    successDiv = contactRequestForm.parentElement.querySelector('[data-name-form="success"]');
    submitButton = contactRequestForm.querySelector('.form-submit-button');

    // Hide status messages initially
    if (errorDiv) errorDiv.style.display = 'none';
    if (successDiv) successDiv.style.display = 'none';

    // Attach submit handler
    contactRequestForm.addEventListener('submit', handleFormSubmission);
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
}


